import { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { API } from "@/App";

/**
 * Fetches the per-SKU Remaining Qty map for the week containing `orderDate`.
 * Used by Create / Edit Quotation forms to render real-time inventory validation.
 * Pass `excludeId` when editing an existing quotation so its own prior contribution
 * is removed from the consumption tally (a quotation should never block itself).
 *
 * Returns:
 *   remaining     – { [sku_id]: number }   (only present when has_allocation === true)
 *   hasAllocation – boolean — false means no allocation row exists for this week,
 *                   in which case the gate silently allows everything (matches backend).
 *   loading       – initial fetch in-flight
 *   refetch       – manual refresh (e.g. after order_date change).
 */
export function useInventoryRemaining(orderDate, excludeId = null) {
  const [remaining, setRemaining] = useState({});
  const [hasAllocation, setHasAllocation] = useState(false);
  const [loading, setLoading] = useState(false);

  const dateKey = useMemo(() => (orderDate ? String(orderDate).slice(0, 10) : ""), [orderDate]);

  useEffect(() => {
    let cancelled = false;
    if (!dateKey) {
      setRemaining({});
      setHasAllocation(false);
      return () => {};
    }
    setLoading(true);
    const params = { order_date: dateKey };
    if (excludeId) params.exclude_id = excludeId;

    axios
      .get(`${API}/inventory-allocations/remaining`, { params })
      .then((res) => {
        if (cancelled) return;
        setRemaining(res.data?.remaining || {});
        setHasAllocation(Boolean(res.data?.has_allocation));
      })
      .catch(() => {
        if (cancelled) return;
        // Fail open — if the lookup fails we don't block the user; backend still gates on save.
        setRemaining({});
        setHasAllocation(false);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [dateKey, excludeId]);

  return { remaining, hasAllocation, loading };
}

/**
 * Warehouse-aware availability for the quotation form (Step 2).
 * Given the selected Pickup Warehouse, fetches the per-SKU Available Qty map:
 *   - FBW warehouse  -> that warehouse's Available Qty (Inventory - Consumed)
 *   - Gurugram/weekly -> Remaining Qty for the week of `orderDate`
 * Quantities are strictly per-warehouse and re-fetched whenever warehouseId/orderDate changes.
 *
 * Returns:
 *   available     – { [sku_id]: number }  (present when hasInventory === true)
 *   hasInventory  – boolean — false means nothing configured for that warehouse/week,
 *                   so the gate silently allows everything (matches backend).
 *   warehouseType – 'fbw_inventory' | 'weekly_allocation' | null
 *   loading       – fetch in-flight
 */
export function useWarehouseAvailability(warehouseId, orderDate, excludeId = null) {
  const [available, setAvailable] = useState({});
  const [hasInventory, setHasInventory] = useState(false);
  const [warehouseType, setWarehouseType] = useState(null);
  const [loading, setLoading] = useState(false);

  const dateKey = useMemo(() => (orderDate ? String(orderDate).slice(0, 10) : ""), [orderDate]);

  useEffect(() => {
    let cancelled = false;
    if (!warehouseId) {
      setAvailable({});
      setHasInventory(false);
      setWarehouseType(null);
      return () => {};
    }
    setLoading(true);
    const params = { warehouse_id: warehouseId };
    if (dateKey) params.order_date = dateKey;
    if (excludeId) params.exclude_id = excludeId;

    axios
      .get(`${API}/quotations/inventory-availability`, { params })
      .then((res) => {
        if (cancelled) return;
        setAvailable(res.data?.available || {});
        setHasInventory(Boolean(res.data?.has_inventory));
        setWarehouseType(res.data?.warehouse_type || null);
      })
      .catch(() => {
        if (cancelled) return;
        // Fail open — backend still gates on save.
        setAvailable({});
        setHasInventory(false);
        setWarehouseType(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [warehouseId, dateKey, excludeId]);

  return { available, hasInventory, warehouseType, loading };
}


/**
 * Given the sale + freebie line items and the remaining-qty map, returns:
 *   violationsBySku – { [sku_id]: { required: number, remaining: number } }
 *   hasViolations   – boolean
 *
 * A SKU is in violation when the SUM of its quantities across sale + freebie rows
 * exceeds its Remaining Qty. When `hasAllocation === false` no violations are reported
 * (matches the backend's "no allocation configured → silently allow" semantics).
 */
export function computeInventoryViolations(saleItems, freebieItems, remaining, hasAllocation) {
  if (!hasAllocation) return { violationsBySku: {}, hasViolations: false };

  const requested = {};
  for (const row of [...(saleItems || []), ...(freebieItems || [])]) {
    const sku = row?.sku_id;
    if (!sku) continue;
    const qty = Number(row?.quantity) || 0;
    requested[sku] = (requested[sku] || 0) + qty;
  }

  const violationsBySku = {};
  for (const [sku, req] of Object.entries(requested)) {
    // STRICT mode: any SKU absent from the week's allocation roster is treated as
    // 0 remaining (must mirror backend assert_inventory_available semantics exactly).
    const rem = typeof remaining[sku] === "number" ? remaining[sku] : 0;
    if (req > rem) {
      violationsBySku[sku] = { required: req, remaining: rem };
    }
  }

  return { violationsBySku, hasViolations: Object.keys(violationsBySku).length > 0 };
}
